import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { request } from "shared/api/request"
import { uploadToPresignedUrl } from "shared/api/uploadToPresignedUrl"
import { Logger } from "shared/lib/Logger"
import { generateUUID } from "shared/lib/generateUUID"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { ToastType } from "shared/types/Toast"
import { AttachmentType } from "shared/types/__generated__/graphql"

import { FeedbackAttachment } from "features/feedback/model/useFeedback"

import {
  UploadResponseItem,
  UploadResponseSchema
} from "../types/FeedbackImageUpload"
import { useFeedbackImageRules } from "./useFeedbackImageRules"

interface UploadedFile {
  attachment: AttachmentType
  s3Key: string
  fileName: string
}

interface UseFeedbackImageUploadParams {
  onAttachmentsChange: (attachments: FeedbackAttachment[]) => void
  currentAttachments: FeedbackAttachment[]
}

export function useFeedbackImageUpload({
  onAttachmentsChange,
  currentAttachments
}: UseFeedbackImageUploadParams) {
  const { t } = useTranslation()
  const { rules, isLoading: isLoadingRules } = useFeedbackImageRules()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  const validateFile = useEvent((file: File): string | null => {
    const maxSizeBytes = rules.maxFileSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      return t("Feedback.ImageTooLarge", { maxSize: rules.maxFileSizeMB })
    }

    return null
  })

  const getPresignedUrl = useEvent(
    async (file: File): Promise<UploadResponseItem | null> => {
      try {
        const response = await request(
          `${import.meta.env.VITE_API_BASE}/feedback/uploads`,
          {
            method: "POST",
            body: JSON.stringify({
              files: [{ fileName: file.name }]
            }),
            headers: {
              "content-type": "application/json"
            }
          }
        )

        if (!response?.ok) {
          throw new Error("Failed to get presigned URL")
        }

        const json = await response.json()
        const parseResult = UploadResponseSchema.safeParse(json)

        if (!parseResult.success) {
          Logger.error(
            parseResult.error,
            "Failed to validate presigned URL response"
          )
          throw new Error("Invalid presigned URL response format")
        }

        return parseResult.data.uploads[0] || null
      } catch (error) {
        throw new Error(
          `Failed to get presigned URL for file: ${file.name}: ${error}`
        )
      }
    }
  )

  const handleUpload = useEvent(
    async (filePreview: FilePreview): Promise<ApiResponse<unknown>> => {
      const { file } = filePreview

      // Validate file
      const validationError = validateFile(file)
      if (validationError) {
        raiseToast({
          message: validationError,
          type: ToastType.ERROR
        })
        return {
          data: null,
          errors: [{ message: validationError }]
        }
      }

      // Step 1: Get presigned POST data from backend
      const uploadResponse = await getPresignedUrl(file)
      if (!uploadResponse) {
        const errorMessage = t("Feedback.UploadFailed")
        raiseToast({
          message: errorMessage,
          type: ToastType.ERROR
        })
        return {
          data: null,
          errors: [{ message: errorMessage }]
        }
      }

      // Step 2: Upload file binary to S3 using presigned POST
      try {
        await uploadToPresignedUrl(uploadResponse.writeUrl, file)
      } catch {
        const errorMessage = t("Feedback.UploadFailed")
        raiseToast({
          message: errorMessage,
          type: ToastType.ERROR
        })
        return {
          data: null,
          errors: [{ message: errorMessage }]
        }
      }

      // Success - store the attachment metadata in form state and create attachment for display
      const [name, extension] = file.name.split(/\.(?=[^.]+$)/)
      const attachment: AttachmentType = {
        id: generateUUID(),
        name: name || file.name,
        fileName: file.name,
        extension: extension || "",
        readUrl: "",
        __typename: "AttachmentType"
      }

      // Create feedback attachment object
      const feedbackAttachment: FeedbackAttachment = {
        s3Key: uploadResponse.s3Key,
        fileName: uploadResponse.fileName
      }

      // Add to uploaded files
      const newUploadedFile: UploadedFile = {
        attachment,
        s3Key: uploadResponse.s3Key,
        fileName: uploadResponse.fileName
      }
      setUploadedFiles(prev => [...prev, newUploadedFile])

      // Update form with all attachments
      onAttachmentsChange([...currentAttachments, feedbackAttachment])

      return { data: attachment }
    }
  )

  const handleDelete = useEvent(async (file: AttachmentType) => {
    // Find the uploaded file metadata
    const uploadedFile = uploadedFiles.find(f => f.attachment.id === file.id)
    if (!uploadedFile) return

    // Remove from uploaded files
    setUploadedFiles(prev => prev.filter(f => f.attachment.id !== file.id))

    // Update form by removing this attachment
    const updatedAttachments = currentAttachments.filter(
      att => att.s3Key !== uploadedFile.s3Key
    )
    onAttachmentsChange(updatedAttachments.length > 0 ? updatedAttachments : [])
  })

  const displayFiles = uploadedFiles.map(f => f.attachment)

  // Format display strings
  const dataFormats = useMemo(() => {
    return [...new Set(Object.values(rules.allowedExtensions).flat())].map(
      ext => ext.replace(/^\./, "").toUpperCase()
    )
  }, [rules.allowedExtensions])

  return {
    displayFiles,
    dataFormats,
    isLoading: isLoadingRules,
    accept: rules.allowedExtensions,
    maxFilesCount: rules.maxFilesCount,
    handleUpload,
    handleDelete
  }
}
